import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar/constants'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {PageHeader} from '@primer/react'
import type {ReactNode} from 'react'

import {getRepoIssuesSidebarPanes} from './get-repo-issues-sidebar-panes'
import {RepoIssuesSidebar} from './RepoIssuesSidebar'

const SIDEBAR_ARIA_LABEL = 'Issues sidebar navigation'
const DEFAULT_SIDEBAR_DIALOG_TITLE = 'Issues'

interface RepoIssuesPageLayoutProps {
  children: ReactNode
  /** Page title for the header. Pass `null` (or omit when no actions either) to suppress the header. */
  title?: ReactNode
  /** Trailing actions for the page header (typically a primary CTA). */
  actions?: ReactNode
  /** Title for the narrow-viewport sidebar Dialog. */
  dialogTitle?: string
}

/**
 * Layout wrapper for every repo-scoped issues page. When
 * `repo_issues_sidebar_layout` is on, renders the new `<SidebarPageLayout>`,
 * with an optional `<PageHeader>` if a `title` or `actions` prop is passed
 * (only `IssueIndexPage` does — the other pages render their own inline
 * `<h1>` from their content components). When the flag is off, falls back to
 * the legacy `<ThreePanesLayout>` via `getRepoIssuesSidebarPanes()` (which
 * itself respects `issue_pinned_views` and `issues_react_collapsible_sidebar`
 * for the legacy sidebar variants).
 */
export function RepoIssuesPageLayout({
  children,
  title,
  actions,
  dialogTitle = DEFAULT_SIDEBAR_DIALOG_TITLE,
}: RepoIssuesPageLayoutProps) {
  const useSidebarPageLayout = isFeatureEnabled('repo_issues_sidebar_layout')
  const showHeader = useSidebarPageLayout && (title != null || actions != null)

  if (useSidebarPageLayout) {
    return (
      <SidebarPageLayout storageKey={SIDEBAR_STORAGE_KEY}>
        {showHeader ? (
          <SidebarPageLayout.Header>
            <PageHeader>
              {title != null && (
                <PageHeader.TitleArea>
                  <PageHeader.Title as="h1">{title}</PageHeader.Title>
                </PageHeader.TitleArea>
              )}
              {actions != null && <PageHeader.Actions>{actions}</PageHeader.Actions>}
            </PageHeader>
          </SidebarPageLayout.Header>
        ) : null}
        <SidebarPageLayout.Sidebar
          aria-label={SIDEBAR_ARIA_LABEL}
          dialogTitle={dialogTitle}
          footer={({isExpanded}) => (
            <>
              <CollapsibleFeedbackLink isExpanded={isExpanded} />
              <CollapsibleSidebar.Toggle />
            </>
          )}
        >
          <RepoIssuesSidebar collapsible />
        </SidebarPageLayout.Sidebar>
        <SidebarPageLayout.Content as="div">{children}</SidebarPageLayout.Content>
      </SidebarPageLayout>
    )
  }

  const panes = getRepoIssuesSidebarPanes()
  return (
    <ThreePanesLayout
      contentAs="div"
      resizeable={false}
      leftPaneWidth="medium"
      leftPane={panes.leftPane}
      collapsibleLeftPane={panes.collapsibleLeftPane}
      middlePane={<>{children}</>}
    />
  )
}
